import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import {
  exportPermission, hasPermission, hasSomePermission, manageSurveyPermission, researchUserPermission
} from 'unihow-common';
import { addErrorMessageAction, setTaskFulfilled, setTaskPending } from '../../../actions';
import { exportSurveyData } from '../../../api';
import ExportDataModal from './ExportDataModal';
import PopUpMenu from '../../PopUpMenu';
import menuItem from '../../PopUpMenu/menuItem';
import styles from './exportIconButton.module.css';
import { withLang } from '../../../i18n';
import useCurrentUser from '../../../hooks/useCurrentUser';

const ExcelExportButton = ({
  surveyId,
  surveySchedule,
  surveyInfo,
  translate: t
}) => {
  const dispatch = useDispatch();
  const { permissions: userPermissions } = useCurrentUser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  // We only want to clear the input on successful submits. Would have been better
  // to implement this on the modal component scope but couldn't get it to work.
  const [clearTextArea, setClearTextArea] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleSubmit = (exportReason) => {
    dispatch(setTaskPending());
    exportSurveyData(surveyId, true, exportReason)
      .then(() => {
        handleCloseModal();
        setClearTextArea(true);
      })
      .catch((error) => {
        setClearTextArea(false);
        dispatch(addErrorMessageAction(error));
      })
      .finally(() => dispatch(setTaskFulfilled()));
  };

  const getExportMenuItems = () => {
    const menuItems = [];
    const { ended } = surveySchedule;
    const { hasEnoughResponses, researchConsentGivenCount, type } = surveyInfo;

    if (hasSomePermission(userPermissions, [
      manageSurveyPermission(type),
      exportPermission(type)
    ])) {
      menuItems.push(menuItem(
        t,
        (id) => exportSurveyData(id),
        'survey.export.excel',
        !(ended && hasEnoughResponses)
      ));
    }

    if (hasPermission(userPermissions, researchUserPermission(type))) {
      menuItems.push(menuItem(
        t,
        handleOpenModal,
        'survey.export.sensitiveExcel',
        !(ended && researchConsentGivenCount)
      ));
    }
    return menuItems;
  };

  return (
    <>
      <PopUpMenu
        className={styles.exportPopupContainer}
        menuItems={getExportMenuItems()}
        hostId={surveyId}
      >
        <button
          type="button"
          className="actionButton icon--contentlink"
          title={t('survey.export.title')}
          data-cy="survey-export-button"
        />
      </PopUpMenu>
      <ExportDataModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        clearTextArea={clearTextArea}
      />
    </>
  );
};

const {
  string, shape, func
} = PropTypes;

ExcelExportButton.propTypes = {
  surveyId: string.isRequired,
  surveyInfo: shape(),
  surveySchedule: shape(),
  translate: func.isRequired
};

ExcelExportButton.defaultProps = {
  surveyInfo: null,
  surveySchedule: null
};

export default withLang(ExcelExportButton);
